import {
	getLocalizedField,
	type SupportedLocale,
} from '#app/utils/blog-i18n.ts'
import {
	DEFAULT_LANGUAGE,
	SUPPORTED_LANGUAGES,
	localizedPath,
} from '#app/utils/i18n.ts'
import { getBlogImgSrc } from '#app/utils/misc.tsx'

/**
 * Build the canonical blog post URL for a given locale.
 * Default locale (de) uses the unprefixed path: /blog/{slug}
 * Other locales use: /{locale}/blog/{slug}
 */
export function blogPostCanonicalPath(
	locale: SupportedLocale,
	slug: string,
): string {
	const blogPath = `/blog/${slug}`
	return locale === DEFAULT_LANGUAGE ? blogPath : localizedPath(locale, blogPath)
}

/**
 * Generate canonical and hreflang link descriptors for a blog post.
 * Returns an array compatible with React Router's meta function (tagName: 'link').
 */
export function generateCanonicalLinks({
	slug,
	locale,
	siteUrl,
}: {
	slug: string
	locale: SupportedLocale
	siteUrl: string
}): Array<{ tagName: 'link'; rel: string; href: string; hrefLang?: string }> {
	const base = siteUrl.replace(/\/$/, '')
	const canonicalHref = `${base}${blogPostCanonicalPath(locale, slug)}`

	const links: Array<{
		tagName: 'link'
		rel: string
		href: string
		hrefLang?: string
	}> = [{ tagName: 'link', rel: 'canonical', href: canonicalHref }]

	for (const lang of SUPPORTED_LANGUAGES) {
		links.push({
			tagName: 'link',
			rel: 'alternate',
			hrefLang: lang,
			href: `${base}${blogPostCanonicalPath(lang, slug)}`,
		})
	}

	links.push({
		tagName: 'link',
		rel: 'alternate',
		hrefLang: 'x-default',
		href: `${base}${blogPostCanonicalPath(DEFAULT_LANGUAGE, slug)}`,
	})

	return links
}

const OG_FALLBACK_IMAGE = '/img/os/hq/logo-mit-text-horizontal.png'
const OG_FALLBACK_ALT = 'Open Sourced Blog'

const PUBLISHER = {
	'@type': 'Organization' as const,
	name: 'Open Sourced GmbH',
	logo: {
		'@type': 'ImageObject' as const,
		url: '/favicons/android-chrome-512x512.png',
	},
}

type BlogPostForSeo = {
	slug: string
	titleDe: string
	titleFr: string | null
	titleEn: string | null
	excerptDe: string | null
	excerptFr: string | null
	excerptEn: string | null
	publishedAt: string | Date | null
	updatedAt: string | Date | null
	author: { name: string | null; username: string }
	category: {
		nameDe: string
		nameFr: string | null
		nameEn: string | null
		slug: string
	} | null
	tags: Array<{
		nameDe: string
		nameFr: string | null
		nameEn: string | null
		slug: string
	}>
	heroImage: { objectKey: string; altText: string | null } | null
}

/**
 * Generate JSON-LD BlogPosting structured data for a blog post.
 */
export function generateBlogPostJsonLd({
	post,
	locale,
	siteUrl,
}: {
	post: BlogPostForSeo
	locale: SupportedLocale
	siteUrl: string
}) {
	const title = getLocalizedField(post, 'title', locale)
	const excerpt = getLocalizedField(post, 'excerpt', locale)
	const postUrl = `${siteUrl}${localizedPath(locale, `/blog/${post.slug}`)}`
	const authorName = post.author.name || post.author.username

	const jsonLd: Record<string, unknown> = {
		'@context': 'https://schema.org',
		'@type': 'BlogPosting',
		headline: title,
		description: excerpt || undefined,
		mainEntityOfPage: { '@type': 'WebPage', '@id': postUrl },
		author: { '@type': 'Person', name: authorName },
		publisher: {
			...PUBLISHER,
			logo: {
				...PUBLISHER.logo,
				url: `${siteUrl}${PUBLISHER.logo.url}`,
			},
		},
		inLanguage: locale,
	}

	jsonLd.image = post.heroImage
		? `${siteUrl}${getBlogImgSrc(post.heroImage.objectKey)}`
		: `${siteUrl}${OG_FALLBACK_IMAGE}`

	if (post.publishedAt) {
		jsonLd.datePublished = new Date(post.publishedAt).toISOString()
	}

	if (post.updatedAt) {
		jsonLd.dateModified = new Date(post.updatedAt).toISOString()
	}

	if (post.tags.length > 0) {
		jsonLd.keywords = post.tags.map((tag) =>
			getLocalizedField(tag, 'name', locale),
		)
	}

	if (post.category) {
		jsonLd.articleSection = getLocalizedField(post.category, 'name', locale)
	}

	return jsonLd
}

/**
 * Generate complete meta tags (OG + Twitter Card) for a blog post.
 * Returns an array compatible with React Router's meta function.
 */
export function generateBlogPostMeta({
	post,
	locale,
	siteUrl,
}: {
	post: BlogPostForSeo
	locale: SupportedLocale
	siteUrl: string
}) {
	const title = getLocalizedField(post, 'title', locale)
	const excerpt = getLocalizedField(post, 'excerpt', locale)
	const postUrl = `${siteUrl}${localizedPath(locale, `/blog/${post.slug}`)}`
	const heroImageUrl = post.heroImage
		? `${siteUrl}${getBlogImgSrc(post.heroImage.objectKey)}`
		: `${siteUrl}${OG_FALLBACK_IMAGE}`
	const heroImageAlt = post.heroImage?.altText ?? OG_FALLBACK_ALT

	const meta: Array<Record<string, unknown>> = [
		{ title: `${title} | Blog | Open Sourced` },
		{ name: 'description', content: excerpt },
		// Open Graph
		{ property: 'og:title', content: title },
		{ property: 'og:description', content: excerpt },
		{ property: 'og:type', content: 'article' },
		{ property: 'og:url', content: postUrl },
	]

	meta.push(
		{ property: 'og:image', content: heroImageUrl },
		{ property: 'og:image:alt', content: heroImageAlt },
	)

	if (post.publishedAt) {
		meta.push({
			property: 'article:published_time',
			content: new Date(post.publishedAt).toISOString(),
		})
	}

	if (post.updatedAt) {
		meta.push({
			property: 'article:modified_time',
			content: new Date(post.updatedAt).toISOString(),
		})
	}

	if (post.author.name) {
		meta.push({ property: 'article:author', content: post.author.name })
	}

	if (post.category) {
		meta.push({
			property: 'article:section',
			content: getLocalizedField(post.category, 'name', locale),
		})
	}

	for (const tag of post.tags) {
		meta.push({
			property: 'article:tag',
			content: getLocalizedField(tag, 'name', locale),
		})
	}

	// Twitter Card
	meta.push(
		{ name: 'twitter:card', content: 'summary_large_image' },
		{ name: 'twitter:title', content: title },
		{ name: 'twitter:description', content: excerpt },
	)
	meta.push(
		{ name: 'twitter:image', content: heroImageUrl },
		{ name: 'twitter:image:alt', content: heroImageAlt },
	)

	// Canonical + hreflang
	meta.push(
		...generateCanonicalLinks({ slug: post.slug, locale, siteUrl }),
	)

	return meta
}

/**
 * Build a canonical path for a given locale.
 * Default locale (de) uses the unprefixed path, others get a /{locale} prefix.
 */
function canonicalPath(locale: SupportedLocale, path: string): string {
	return locale === DEFAULT_LANGUAGE ? path : localizedPath(locale, path)
}

/**
 * Generate BreadcrumbList JSON-LD structured data for a blog post.
 * With category:    Home → Blog → Category → Post
 * Without category: Home → Blog → Post
 */
export function generateBreadcrumbJsonLd({
	post,
	locale,
	siteUrl,
}: {
	post: Pick<BlogPostForSeo, 'slug' | 'titleDe' | 'titleFr' | 'titleEn' | 'category'>
	locale: SupportedLocale
	siteUrl: string
}) {
	const base = siteUrl.replace(/\/$/, '')
	const title = getLocalizedField(post, 'title', locale)

	const items: Array<{ name: string; url: string }> = [
		{ name: 'Home', url: `${base}${canonicalPath(locale, '/')}` },
		{ name: 'Blog', url: `${base}${canonicalPath(locale, '/blog')}` },
	]

	if (post.category) {
		items.push({
			name: getLocalizedField(post.category, 'name', locale),
			url: `${base}${canonicalPath(locale, `/blog/category/${post.category.slug}`)}`,
		})
	}

	items.push({
		name: title,
		url: `${base}${blogPostCanonicalPath(locale, post.slug)}`,
	})

	return {
		'@context': 'https://schema.org',
		'@type': 'BreadcrumbList',
		itemListElement: items.map((item, index) => ({
			'@type': 'ListItem',
			position: index + 1,
			name: item.name,
			item: item.url,
		})),
	}
}

/**
 * Generate JSON-LD Blog schema for the blog list page.
 */
export function generateBlogListJsonLd({
	siteUrl,
	locale,
	description,
}: {
	siteUrl: string
	locale: SupportedLocale
	description: string
}) {
	const blogUrl = `${siteUrl}${localizedPath(locale, '/blog')}`

	return {
		'@context': 'https://schema.org',
		'@type': 'CollectionPage',
		name: 'Blog | Open Sourced',
		description,
		url: blogUrl,
		publisher: {
			...PUBLISHER,
			logo: {
				...PUBLISHER.logo,
				url: `${siteUrl}${PUBLISHER.logo.url}`,
			},
		},
		inLanguage: locale,
	}
}
